import { FC } from 'react';
import { logger } from '@belong/logging';
import { ICatalogOfferReward, IDictionary } from '@belong/types';
import { IAppliedOfferReward } from '@belong/types/api/offers';
import { OFFER_TEMPLATE_TYPE, rewardToDictionary, useOfferTemplate } from '@belong/offers';
import { OfferAlert } from '../OfferAlert';
import { ErrorState } from './ErrorState';
import { LoadingState } from './LoadingState';
import Callout from '../Callout';

interface IOfferAlertWithTemplate {
  offerId: string;
  reward: IAppliedOfferReward | ICatalogOfferReward;
  templateType: OFFER_TEMPLATE_TYPE;
  dictionary?: IDictionary;
}

// Wrapper component to load an Offer Template and output an OfferAlert
export const OfferAlertWithTemplate: FC<IOfferAlertWithTemplate> = props => {
  const { offerId, reward, templateType, dictionary = {} } = props;
  const { isLoading, error, template } = useOfferTemplate(reward, templateType);

  if (error) {
    return <ErrorState offerId={offerId} />;
  }

  if (isLoading) {
    // Waiting for template load:
    return <LoadingState offerId={offerId} />;
  }

  if (templateType === OFFER_TEMPLATE_TYPE.ACTIVATED) {
    // Activated template are only intended to be used with AppliedRewardAlert components
    logger.error(`OfferAlertWithTemplate should not be used with 'Activated' templates`);
    return <ErrorState offerId={offerId} />;
  }

  // Failed to load Template:
  if (!template) {
    return <ErrorState offerId={offerId} />;
  }

  const rewardDictionary: IDictionary = { ...dictionary, ...rewardToDictionary(reward), 'offer:id': offerId };

  // PlanCard OfferAlerts only show the Callout from the template
  if (templateType === OFFER_TEMPLATE_TYPE.PLAN_CARD) {
    return (
      <Callout {...template} data-testid="offer-alert" data-offerid={offerId} extendedDictionary={rewardDictionary} />
    );
  }

  return <OfferAlert {...template} extendedDictionary={rewardDictionary} />;
};

OfferAlertWithTemplate.displayName = 'OfferAlertWithTemplate';
